// SPDX-FileCopyrightText: Copyright 2024 Dash0 Inc.

import { LucideIcon, SearchIcon, XIcon } from "lucide-react";
import React, { forwardRef, KeyboardEvent, ReactNode } from "react";

// import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { cn } from "@/lib/utils";

type SearchWithoutBehaviorProps = {
	value?: string;
	defaultValue?: string;
	placeholder?: string;
	onChange(value: string): void;
	onKeyDown?(e: KeyboardEvent<HTMLInputElement>): void;
	onClearClick(): void;
	className?: string;
	inputClassName?: string;
	InputIcon?: LucideIcon | null;
	additionalContent?: ReactNode;
	/**
	 * Shows a loading spinner in the search input.
	 */
	isLoading?: boolean;
	/**
	 * Show clear button
	 */
	showClear?: boolean;

	autoFocus?: boolean;
};

/**
 * This component implements the visual styling of our search input box, without
 * any behavior. This is useful for when you want to implement your own behavior
 * for the search input box. For example, we are using a separate behavior
 * within the resource tree next to the resource table.
 */
export const SearchWithoutBehavior = forwardRef<HTMLInputElement, SearchWithoutBehaviorProps>(
	function SearchWithoutBehavior(
		{
			value,
			defaultValue,
			placeholder,
			onChange,
			onKeyDown,
			onClearClick,
			className,
			inputClassName,
			InputIcon,
			additionalContent,
			isLoading,
			autoFocus,
			showClear,
		},
		ref
	) {
		const InputIconWithFallback = InputIcon || SearchIcon;
		return (
			<div
				className={cn(
					"flex h-8 items-center gap-2 rounded-md border border-input bg-input px-2 [&:focus-within]:border-input-icon",
					className
				)}
			>
				{/* Allow disabling the icon by setting it to `null` */}
				{InputIcon !== null && <InputIconWithFallback className="h-4 w-4 shrink-0 text-input-icon" />}
				<Input
					ref={ref}
					size="xs"
					value={value}
					placeholder={placeholder}
					className={cn(
						"border-0! focus-visible:border-0 focus-visible:ring-0 focus-visible:ring-offset-0",
						inputClassName
					)}
					onChange={(e) => onChange(e.target.value)}
					onKeyDown={onKeyDown}
					defaultValue={defaultValue}
					autoFocus={autoFocus}
				/>
				{isLoading != null && (
					<LoadingSpinner
						size="16"
						className={cn("opacity-0 transition-opacity duration-300", {
							"opacity-100": isLoading,
						})}
						style={{ animationDuration: "700ms" }}
						disableSpinning={!isLoading}
					/>
				)}
				{showClear && (
					// <IconButton
					// 	size="xs"
					// 	variant="transparent"
					// 	disabled={isEmpty(value) && isEmpty(defaultValue)}
					// 	onClick={onClearClick}
					// >
						<XIcon onClick={onClearClick} />
					// </IconButton>
				)}
				{additionalContent}
			</div>
		);
	}
);
